import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DropdownItem, ToggleButton } from '@frk/eds-components';
import {
  FundAttributionCustomDataFetch,
  FundAttributionDownloadableGridData,
  FundAttributionGridDataTypes,
  FundAttributionOtherGridData,
  FundAttributionPeriodDates,
  FundAttributionRegionGridData,
  FundAttributionSectorGridData,
  FundAttributionTimePeriod,
  FundAttributionTotalRegionGridData,
  FundAttributionTotalSectorGridData,
} from '@products/types/fund-attribution.type';
import { TranslateService } from '@shared/translate/translate.service';
import { SwitchProps, SwitchState } from '@types';

@Component({
  selector: 'ft-timeperiod-data',
  templateUrl: './timeperiod-data.component.html',
  styleUrls: ['./timeperiod-data.component.scss'],
})
export class TimeperiodDataComponent implements OnInit, OnChanges {
  @Input() periodDates: FundAttributionPeriodDates[] = [];
  @Input() calendarYears: FundAttributionPeriodDates[] = [];
  @Input() timePeriodData: FundAttributionGridDataTypes;
  @Input() showCurrencyAttribution: boolean;
  @Input() selectedAsOfDateStd: string;
  @Input() selectedGridDataType: string;
  @Input() tabNumber: FundAttributionTimePeriod;
  @Input() fundName: string;
  @Input() bmName: string;
  @Output()
  fetchAttributionData = new EventEmitter<FundAttributionCustomDataFetch>();
  @Output()
  triggerDownloadAction = new EventEmitter<FundAttributionDownloadableGridData>();

  periodList: DropdownItem[] = [];
  periodDropdownTitleText: string;
  currencyToggleSwitch: SwitchProps;
  gridDataTypeToggle: ToggleButton[] = [];
  selectedAsOfDate: string;
  selectedSectorTotalData: FundAttributionTotalSectorGridData &
    FundAttributionOtherGridData;
  selectedCountryTotalData: FundAttributionTotalRegionGridData &
    FundAttributionOtherGridData;
  isExpanded = false;
  isWholeGridEmptyForSelectedDate = false; // Data not available for 'both' grid type for selected 'asOfDate'.

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.getPeriodList();
    this.periodDropdownTitleText = this.getPeriodDropdownTitleText();
    this.populate();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (!simpleChanges.timePeriodData.isFirstChange()) {
      this.populate();
    }
  }

  /**
   * Assign grid bindings from component input.
   */
  private populate(): void {
    this.selectedAsOfDate = this.selectedAsOfDateStd
      ? this.periodDates.find(
          (date) => date.asOfDateStd === this.selectedAsOfDateStd
        )?.asOfDate
      : this.periodDates[0]?.asOfDate;

    this.currencyToggleSwitch = {
      labelKey: this.translateService.instant(
        'products.attribution-switch-currency'
      ),
      labelOnKey: this.translateService.instant('products.on'),
      labelOffKey: this.translateService.instant('products.off'),
      state: this.showCurrencyAttribution ? SwitchState.ON : SwitchState.OFF,
    };

    this.selectedSectorTotalData = this.timePeriodData?.sectorsTotalData;
    this.selectedCountryTotalData = this.timePeriodData?.regionsTotalData;

    // Grid data type toggle values would be updated, once selected data is binded.
    // So, toggle options would be one or two depending upon data inside sector/country.
    this.getGridDataTypeToggleValues();
  }

  /**
   * Transform period list from state as per DropdownItem[].
   */
  private getPeriodList(): void {
    if (this.tabNumber === FundAttributionTimePeriod.CALENDAR_YEAR) {
      this.calendarYears.forEach((calendarYear) => {
        this.periodList.push({
          label: calendarYear.year,
          value: calendarYear.asOfDateStd,
        });
      });
    } else {
      this.periodDates.forEach((period) => {
        this.periodList.push({
          label: period.asOfDate,
          value: period.asOfDateStd,
        });
      });
    }
  }

  /**
   * Return the title text for the option selected.
   * @param optionSelected option selected
   */
  private getPeriodDropdownTitleText(): string {
    return this.periodList.find(
      (period) => period.value === this.periodList[0].value
    )?.label;
  }

  /**
   * Get grid data types toggle values as per grid data availability for asOfDate selected.
   */
  private getGridDataTypeToggleValues(): void {
    // First, empty the current toggle list.
    this.gridDataTypeToggle = [];

    // Then, push toggle options as per sector/country data availability.
    this.gridDataTypeToggle.push({
      text: this.translateService.instant(
        'products.attribution-sector-industry'
      ),
      disabled: this.selectedSectorTotalData?.sectors.length ? false : true,
      active:
        (this.selectedSectorTotalData?.sectors.length &&
          this.selectedGridDataType === 'sector') ||
        !this.selectedCountryTotalData?.regions.length
          ? true
          : false,
      value: 'sector',
    });

    this.gridDataTypeToggle.push({
      text: this.translateService.instant(
        'products.attribution-region-country'
      ),
      disabled: this.selectedCountryTotalData?.regions.length ? false : true,
      active:
        (this.gridDataTypeToggle[0].disabled && // If first option is 'disabled', then might be second option be marked 'active'.
          this.selectedCountryTotalData?.regions.length) ||
        (this.selectedGridDataType === 'country' &&
          this.selectedCountryTotalData?.regions.length)
          ? true
          : false,
      value: 'country',
    });

    // Also, if either first option is not 'disabled' & gridType from state has value 'sector'
    // Or first option is not 'disabled' & second option is, then show first grid data type.
    // Else if, either first option is 'disabled' & second one is not or gridType from state has value 'country',
    // Then, then show second grid data type.
    // Else, whole grid is empty and don't display grid table at all.
    if (
      (!this.gridDataTypeToggle[0].disabled &&
        this.selectedGridDataType === 'sector') ||
      (!this.gridDataTypeToggle[0].disabled &&
        this.gridDataTypeToggle[1].disabled)
    ) {
      this.selectedGridDataType = 'sector';
      this.isWholeGridEmptyForSelectedDate = false;
    } else if (
      (this.gridDataTypeToggle[0].disabled &&
        !this.gridDataTypeToggle[1].disabled) ||
      this.selectedGridDataType === 'country'
    ) {
      this.selectedGridDataType = 'country';
      this.isWholeGridEmptyForSelectedDate = false;
    } else {
      this.isWholeGridEmptyForSelectedDate = true;
    }
  }

  /**
   * Trigger new grid data when period range is changed.
   * @param selectedOption selected drop down option
   * @param targetView tabular view for which date range has been changed
   */
  onTimePeriodChange(
    selectedOption: DropdownItem,
    targetView: FundAttributionTimePeriod
  ): void {
    // First, make toggle expansion to false.
    this.isExpanded = false;

    // Now, find the selected std date from period list.
    const selectedAsOfDateStd: string = this.periodDates.find(
      (period) => period.asOfDateStd === selectedOption.value
    ).asOfDateStd;

    // Further, get the list as per asOfDate selected for both toggle types.
    this.fetchAttributionData.next({
      targetView,
      selectedAsOfDateStd,
      selectedGridDataType: this.selectedGridDataType,
      showCurrencyAttribution: this.showCurrencyAttribution,
    });
  }

  /**
   * Show/Hide currency attribution grid columns when currency toggle triggers change.
   */
  onCurrencyToggleChanged(): void {
    this.showCurrencyAttribution = !this.showCurrencyAttribution;
  }

  /**
   * Assign new value to selected grid type.
   * Also, make any expansion to false for displayed grid data.
   * @param selectedOption selected drop down option
   */
  onGridDataTypeUpdate(selectedOption: ToggleButton): void {
    this.selectedGridDataType = selectedOption.value;

    if (this.selectedGridDataType === 'sector') {
      this.selectedCountryTotalData.regions.forEach((region) => {
        region.isExpanded = false;
      });
    } else {
      this.selectedSectorTotalData.sectors.forEach((sector) => {
        sector.isExpanded = false;
      });
    }

    this.isExpanded = false;
  }

  /**
   * Toggle all rows at once to expand/collapse.
   * @param event click event
   */
  toggleAll(event: MouseEvent): void {
    event?.preventDefault();

    this.isExpanded = !this.isExpanded;

    if (this.selectedGridDataType === 'sector') {
      this.selectedSectorTotalData.sectors.forEach((sector) => {
        sector.isExpanded = this.isExpanded;
      });
    } else {
      this.selectedCountryTotalData.regions.forEach((region) => {
        region.isExpanded = this.isExpanded;
      });
    }
  }

  /**
   * Toggle selected row to expand/collapse.
   * @param event click event
   * @param gridDataType currently selected grid data type
   * @param row current row to be toggle
   */
  toggleRow(
    event: MouseEvent,
    gridDataType: string,
    row:
      | (FundAttributionSectorGridData & FundAttributionOtherGridData)
      | (FundAttributionRegionGridData & FundAttributionOtherGridData)
  ): void {
    event?.preventDefault();

    if (gridDataType === 'sector') {
      this.selectedSectorTotalData.sectors.find((sector) => {
        if (
          sector.sectorName ===
          (row as FundAttributionSectorGridData).sectorName
        ) {
          sector.isExpanded = sector.isExpanded ? !sector.isExpanded : true;
        }
      });
    } else {
      this.selectedCountryTotalData.regions.find((region) => {
        if (
          region.regionName ===
          (row as FundAttributionRegionGridData).regionName
        ) {
          region.isExpanded = region.isExpanded ? !region.isExpanded : true;
        }
      });
    }
  }

  /**
   * Trigger download action for the grid data.
   */
  onDownloadButtonClicked(): void {
    this.triggerDownloadAction.next({
      fundName: this.fundName,
      selectedAsOfDate: this.selectedAsOfDate,
      selectedSectorTotalData: this.selectedSectorTotalData,
      selectedCountryTotalData: this.selectedCountryTotalData,
    });
  }
}
